import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Chip, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, FormControlLabel, Checkbox, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MedicationIcon from '@mui/icons-material/Medication';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import { getStores, getStockBalances } from '../../api/stockLedger';
import useItemNames from '../../hooks/useItemNames';
import { defaultUom } from '../../utils/itemDisplay';
import { issueUomCode } from '../../utils/uomConversionEntity';
import { useEmrContext } from '../../hooks/useEmrContext';
import { runCreateVerifyReserve } from '../../utils/pharmacyDispenseFlow';
import {
  readPreferredPharmacyStore,
  writePreferredPharmacyStore,
} from '../../utils/pharmacyStorePreference';
import { IPD_COLORS } from '../../theme';
import { fadeInSx, lineTableSx, summaryHighlightSx } from './pharmacyStyles';
import { EmrContextPanel } from './PharmacyUi';

const LINE_DEFAULT = {
  item_id: '', batch_number: '', quantity: '', unit_rate: '', remarks: '', uom_id: '',
};

export default function DispenseForm() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const emr = useEmrContext();
  const { items: catalogItems, itemOptionLabel: labelItem, catalog } = useItemNames();
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [lines, setLines] = useState([{ ...LINE_DEFAULT }]);
  const [allergyChecked, setAllergyChecked] = useState(true);
  const [doseChecked, setDoseChecked] = useState(true);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      // Seeded from the session's remembered pick — same convenience as the
      // start-dispense page; validated against the store list below.
      store_id: readPreferredPharmacyStore(),
      transaction_date: dayjs().format('YYYY-MM-DD'),
      prescription_number: '',
      remarks: '',
      doctor_id: emr.doctor_id || '',
      patient_id: emr.external_patient_id || '',
    },
  });

  const storeId = watch('store_id');
  const validLineCount = lines.filter((l) => l.item_id && Number(l.quantity) > 0).length;

  const medicineItems = useMemo(
    () => catalogItems.filter((i) => i.item_type === 'MEDICINE'),
    [catalogItems],
  );

  useEffect(() => {
    getStores({ is_pharmacy_store: true })
      .then((d) => setStores(Array.isArray(d) ? d : (d?.items || [])))
      .catch(() => {});
  }, []);

  // Validate the remembered store once the list arrives; auto-select when the
  // facility has exactly one pharmacy store. Persist real picks so the next
  // dispense this session starts pre-filled.
  useEffect(() => {
    if (!stores.length) return;
    const exists = stores.some((s) => String(s.store_id || s.id) === String(storeId));
    if (storeId && !exists) {
      setValue('store_id', '');
    } else if (!storeId && stores.length === 1) {
      const only = String(stores[0].store_id || stores[0].id);
      setValue('store_id', only);
      writePreferredPharmacyStore(only);
    } else if (storeId && exists) {
      writePreferredPharmacyStore(storeId);
    }
  }, [stores, storeId, setValue]);

  useEffect(() => {
    if (!storeId) {
      setStockItems([]);
      return;
    }
    getStockBalances({ store_id: storeId })
      .then((d) => setStockItems(Array.isArray(d) ? d : (d?.items || [])))
      .catch(() => {});
  }, [storeId]);

  function stockForItem(itemId) {
    return stockItems.find((s) => String(s.item_id || s.id) === String(itemId));
  }

  function catalogItem(itemId) {
    return medicineItems.find((i) => String(i.item_id || i.id) === String(itemId));
  }

  function updateLine(i, key, val) {
    setLines((l) => l.map((row, idx) => {
      if (idx !== i) return row;
      const updated = { ...row, [key]: val };
      if (key === 'item_id') {
        const stock = stockForItem(val);
        const meta = catalogItem(val);
        updated.uom_id = defaultUom(meta, 'issue') || meta?.uom_id || stock?.uom_id || '';
        if (stock) {
          updated.batch_number = stock.batch_number || '';
          updated.unit_rate = stock.unit_rate ?? stock.mrp ?? '';
        } else {
          updated.batch_number = '';
          updated.unit_rate = '';
        }
      }
      return updated;
    }));
  }

  async function onSubmit(data) {
    const validLines = lines.filter((l) => l.item_id && Number(l.quantity) > 0);
    if (!validLines.length) return;
    if (!allergyChecked || !doseChecked) {
      toast.error('Confirm allergy and dose checks before reserving stock');
      return;
    }

    setSaving(true);
    try {
      const prescription = {
        id: data.prescription_number || undefined,
        patient_id: data.patient_id || emr.external_patient_id,
        doctor_id: data.doctor_id || emr.doctor_id,
        admission_id: emr.external_admission_id || undefined,
        _raw: {
          patient_id: data.patient_id || emr.external_patient_id,
          doctor_id: data.doctor_id || emr.doctor_id,
          admission_id: emr.external_admission_id,
          id: data.prescription_number,
        },
      };

      const flowLines = validLines.map((l) => ({
        item_id: l.item_id,
        uom_id: l.uom_id || catalogItem(l.item_id)?.base_uom_id,
        dispense_qty: l.quantity,
        batch_number: l.batch_number || '',
        unit_rate: l.unit_rate || '0',
        remarks: l.remarks,
      }));

      const { transactionId } = await runCreateVerifyReserve({
        prescription,
        storeId: data.store_id,
        lines: flowLines,
        stores,
        allergyChecked,
        doseChecked,
        remarks: data.remarks || 'Manual pharmacy dispense',
        isDirectDispense: !data.prescription_number,
      });

      toast.success('Dispense created and stock reserved');
      history.push(`${url.replace('/dispense/new', '/dispenses')}/${transactionId}`);
    } catch (e) {
      toast.error(e?.message || 'Failed to create dispense');
    } finally {
      setSaving(false);
    }
  }

  const storeOptions = stores.map((s) => ({ value: s.store_id || s.id, label: s.store_name }));
  const itemOptions = medicineItems.map((i) => ({ value: i.item_id || i.id, label: labelItem(i) }));
  const catalogLoading = !catalog;

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={fadeInSx}>
      <PageHeader
        eyebrow="Pharmacy Management"
        title="New Dispense"
        description="Create → Verify → Reserve stock from the selected pharmacy store."
        actions={
          <>
            <InvButton variant="outlined" tone="teal" onClick={() => history.goBack()}>Cancel</InvButton>
            <InvButton type="submit" loading={saving} disabled={validLineCount === 0 || !allergyChecked || !doseChecked}>
              Verify & Reserve
            </InvButton>
          </>
        }
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <SectionCard title="Dispense Details" subtitle="Transaction header and patient context">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Controller name="store_id" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvSelect label="Pharmacy Store *" value={field.value} onChange={field.onChange}
                      options={storeOptions} error={errors.store_id} />
                  )} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller name="transaction_date" control={control}
                  render={({ field }) => <InvTextField {...field} label="Date" type="date" InputLabelProps={{ shrink: true }} />} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller name="prescription_number" control={control}
                  render={({ field }) => <InvTextField {...field} label="Prescription # / source ref" />} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller name="patient_id" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => <InvTextField {...field} label="Patient ID *" error={errors.patient_id} />} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller name="doctor_id" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => <InvTextField {...field} label="Doctor ID *" error={errors.doctor_id} />} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="remarks" control={control}
                  render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />} />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Pharmacist verification" subtitle="Required before stock is reserved" sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={allergyChecked}
                    onChange={(e) => setAllergyChecked(e.target.checked)}
                    sx={{ color: IPD_COLORS.primary, '&.Mui-checked': { color: IPD_COLORS.primary } }}
                  />
                )}
                label="Allergy check completed"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={doseChecked}
                    onChange={(e) => setDoseChecked(e.target.checked)}
                    sx={{ color: IPD_COLORS.primary, '&.Mui-checked': { color: IPD_COLORS.primary } }}
                  />
                )}
                label="Dose check completed"
              />
            </Box>
          </SectionCard>

          <SectionCard
            title="Medicines to Dispense"
            subtitle={
              catalogLoading
                ? 'Loading medicine catalog…'
                : `${medicineItems.length} medicines in catalog${storeId ? ` · ${stockItems.length} batches in selected store` : ''}`
            }
            sx={{ mt: 2.5 }}
            actions={
              <InvButton size="small" startIcon={<AddIcon />} onClick={() => setLines((l) => [...l, { ...LINE_DEFAULT }])}>
                Add Row
              </InvButton>
            }
          >
            <Box sx={{ ...lineTableSx, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 220 }}>Medicine</TableCell>
                    <TableCell sx={{ width: 130 }}>Batch</TableCell>
                    <TableCell sx={{ width: 90 }}>Qty</TableCell>
                    <TableCell sx={{ width: 100 }}>Unit Rate</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, i) => {
                    const stock = line.item_id ? stockForItem(line.item_id) : null;
                    const issueUnit = line.item_id ? issueUomCode(catalogItem(line.item_id)) : '';
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          <InvSelect
                            label=""
                            value={line.item_id}
                            onChange={(e) => updateLine(i, 'item_id', e.target.value)}
                            options={itemOptions}
                            disabled={catalogLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <InvTextField
                            value={line.batch_number}
                            label=""
                            placeholder={stock ? '' : 'FEFO auto'}
                            onChange={(e) => updateLine(i, 'batch_number', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <InvTextField value={line.quantity} label="" type="number"
                              inputProps={{ min: 0.01, step: 0.01 }}
                              onChange={(e) => updateLine(i, 'quantity', e.target.value)} />
                            {/* The item master's dispense unit, so the number is
                                unambiguous. Not selectable — change it on the item. */}
                            {issueUnit && (
                              <Chip
                                label={issueUnit}
                                size="small"
                                disabled
                                sx={{ height: 22, fontSize: 10.5, fontWeight: 700 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <InvTextField value={line.unit_rate} label="" type="number"
                            inputProps={{ min: 0 }}
                            onChange={(e) => updateLine(i, 'unit_rate', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <InvTextField value={line.remarks} label=""
                            onChange={(e) => updateLine(i, 'remarks', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" disabled={lines.length <= 1}
                            onClick={() => setLines((l) => l.filter((_, idx) => idx !== i))}
                            sx={{ color: IPD_COLORS.error.text, transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.1)' } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            {validLineCount > 0 && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MedicationIcon sx={{ fontSize: 18, color: IPD_COLORS.primary }} />
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                  {validLineCount} medicine{validLineCount > 1 ? 's' : ''} ready to dispense
                </Typography>
              </Box>
            )}
            {!lines.every((l) => !l.item_id || l.uom_id) && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>
                Some lines are missing a unit of measure — pick medicines from the catalog with a base UOM.
              </Alert>
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <EmrContextPanel
            patientId={emr.external_patient_id}
            admissionId={emr.external_admission_id}
            doctorId={emr.doctor_id}
          />
          <Box sx={{ ...summaryHighlightSx, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MedicationIcon sx={{ fontSize: 20, color: IPD_COLORS.primary }} />
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: IPD_COLORS.textPrimary }}>
                Dispense Notes
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, lineHeight: 1.6 }}>
              Leave batch blank to auto-pick by FEFO. After reserve, use Allocate then Deliver on the detail page.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
